/**
 * @fileoverview `/theme` and `/theme/:naddr` -- writing the thing that styles
 * pages.
 *
 * A SECOND WRITING ROUTE, AND THE GATE IS INSIDE IT for the same reason it is
 * inside the page editor: this app's main screen is a public page, so a gate
 * around the app would put a sign-in message in front of every reader who has
 * no account, on the one route the product exists for. See `RequireSigner`.
 *
 * THE EDIT URL HOLDS THE THEME'S ADDRESS, not an event id, because an id would
 * break the first time the theme was saved -- the one moment the link is
 * guaranteed to be used again. That is the same rule as the page editor and the
 * same rule as a page's own link.
 *
 * WHAT IT REFUSES AND SAYS ALOUD: somebody else's theme. This is the writing
 * side, where an author is entitled to the real reason; the reading side's
 * discipline of collapsing every failure into one answer exists to protect a
 * private page's existence and has nothing to protect here, because there is no
 * private theme.
 */

import type { SignerInterface } from '@cloistr/auth';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { RequireSigner } from '@/components/RequireSigner';
import { ThemeEditor, emptyThemeDraft, type ThemeEditorDraft } from '@/components/ThemeEditor';
import { decodeIdentifier } from '@/services/nostr/identifiers';
import { encodeAddress } from '@/services/nostr/pageLinks';
import { relaysFor } from '@/services/nostr/relays';
import { PAGE_THEME_KIND } from '@/services/pages/kinds';
import { loadThemeEvent } from '@/services/pages/loadTheme';
import type { PublishedThemeAddress } from '@/services/pages/publishTheme';
import { parseThemeEvent } from '@/services/pages/theme';

export function ThemeEditorRoute() {
  const params = useParams();

  // CAPTURED ONCE, as the page editor does. Publishing a new theme rewrites the
  // URL to that theme's own address so a reload comes back to it; re-reading the
  // parameter would treat our own rewrite as a navigation and reload the editor
  // out from under the author a moment after they pressed Publish.
  const [naddr] = useState<string | null>(params.naddr ?? null);

  return (
    <RequireSigner>
      {(signer, pubkey) => <Editing naddr={naddr} signer={signer} pubkey={pubkey} />}
    </RequireSigner>
  );
}

type Loaded =
  | { status: 'new' }
  | { status: 'ready'; draft: ThemeEditorDraft }
  | { status: 'unavailable' }
  | { status: 'not-yours' }
  | { status: 'not-a-theme' };

function Editing({
  naddr,
  signer,
  pubkey,
}: {
  naddr: string | null;
  signer: SignerInterface;
  pubkey: string;
}) {
  const [loaded, setLoaded] = useState<Loaded | null>(naddr === null ? { status: 'new' } : null);

  useEffect(() => {
    if (naddr === null) return;
    let live = true;

    // WRAPPED, BECAUSE THIS THROWS FOR AN nsec. A secret key in the URL is not
    // an unknown identifier, it is the worst thing that can be in a URL, and
    // the decoder refuses it loudly rather than returning null. Caught here and
    // collapsed to unavailable so the route never echoes it into a message, a
    // query or a retry.
    let decoded;
    try {
      decoded = decodeIdentifier(naddr);
    } catch {
      setLoaded({ status: 'unavailable' });
      return;
    }
    if (decoded?.type !== 'address') {
      setLoaded({ status: 'unavailable' });
      return;
    }
    // REFUSED BEFORE THE QUERY, not after it. An address naming some other
    // addressable kind is a real reference to something real that is not a
    // theme, and opening it in a theme editor would offer to overwrite it with
    // one. The reader refuses the same thing at the same point, and for the
    // stronger reason that following it would let a page make a browser fetch
    // an arbitrary event.
    if (decoded.kind !== PAGE_THEME_KIND) {
      setLoaded({ status: 'not-a-theme' });
      return;
    }
    if (decoded.pubkey !== pubkey) {
      setLoaded({ status: 'not-yours' });
      return;
    }

    const coordinate = `${decoded.kind}:${decoded.pubkey}:${decoded.identifier}`;
    void loadThemeEvent(coordinate, relaysFor(decoded.relays)).then((event) => {
      if (!live) return;
      const theme = event && parseThemeEvent(event, PAGE_THEME_KIND);
      if (!event || !theme) {
        setLoaded({ status: 'unavailable' });
        return;
      }
      setLoaded({ status: 'ready', draft: draftFrom(event, theme) });
    });

    return () => {
      live = false;
    };
  }, [naddr, pubkey]);

  const onPublished = useCallback((address: PublishedThemeAddress) => {
    // The history API rather than the router, so this does not remount the
    // editor and discard what is still on screen. It only makes the URL
    // describe the state the screen is already in.
    window.history.replaceState(null, '', `/theme/${themeNaddr(address)}`);
  }, []);

  if (loaded === null) {
    return (
      <div className="page-loading" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }

  if (loaded.status === 'unavailable') {
    return (
      <div className="page-unavailable">
        <h1>That theme is not available</h1>
        <p className="page-unavailable-note">
          Nothing at that address could be opened for editing.{' '}
          <Link to="/theme">Start a new theme</Link> instead.
        </p>
      </div>
    );
  }

  if (loaded.status === 'not-yours') {
    return (
      <div className="page-unavailable">
        <h1>That theme is somebody else&rsquo;s</h1>
        <p className="page-unavailable-note">
          A theme can only be edited by the key that published it. You can still point your
          own pages at it: a theme is referenced by address, and editing it is the author&rsquo;s
          to do. <Link to="/theme">Start a theme of your own</Link>.
        </p>
      </div>
    );
  }

  if (loaded.status === 'not-a-theme') {
    return (
      <div className="page-unavailable">
        <h1>That address is not a theme</h1>
        <p className="page-unavailable-note">
          It names an event of another kind. Saving it here would replace it with a theme.{' '}
          <Link to="/theme">Start a new theme</Link>.
        </p>
      </div>
    );
  }

  return (
    <ThemeEditor
      signer={signer}
      initial={loaded.status === 'new' ? emptyThemeDraft() : loaded.draft}
      existing={loaded.status !== 'new'}
      onPublished={onPublished}
    />
  );
}

/**
 * The draft for an existing theme: what the parser understood, plus the tags it
 * did not.
 *
 * THE VALUES COME FROM THE EVENT'S TAGS AND NOT FROM THE PARSED VARIABLES. The
 * parsed form is keyed by CSS custom property, which is our stylesheet's
 * business and not the vocabulary an author edits; going back through it would
 * also mean inverting a mapping that is deliberately one-way. The tags carry
 * the public token names already.
 */
function draftFrom(
  event: { tags: string[][]; content: string },
  theme: { title: string }
): ThemeEditorDraft {
  const tokens: Record<string, string> = {};
  for (const tag of event.tags) {
    if (tag[0] !== 'token') continue;
    const [, name, value] = tag;
    if (typeof name !== 'string' || typeof value !== 'string') continue;
    // First wins, matching the reader. A repeated token is a malformed theme
    // rather than an override, and an editor that took the last would show the
    // author a value their readers are not seeing.
    if (!(name in tokens)) tokens[name] = value;
  }

  return {
    identifier: event.tags.find((t) => t[0] === 'd' && t.length > 1)?.[1] ?? '',
    title: theme.title,
    description: event.content,
    tokens,
    // Everything this screen has no control for, carried back out on save. See
    // `ThemeDraft.carried`: the shipped shape is fully covered today, and the
    // day it is not, the editor that strips the new tag will already exist.
    carried: event.tags.filter((t) => t[0] !== 'd' && t[0] !== 'title' && t[0] !== 'token'),
  };
}

/**
 * A theme's address as an `naddr`, for the edit URL.
 *
 * The page link helper's encoder rather than a second one, so an address this
 * app produces is the same shape wherever it is produced.
 */
function themeNaddr(address: PublishedThemeAddress): string {
  return encodeAddress(address);
}
